import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from '../MemberFormModal/MemberFormModal.module.css';

import { participationStatusOptions } from 'config/admin/athlete';
import { linkAthleteToClub, getSuggestedClubAthleteNumber } from 'api/userApi';
import {
  notifyContactPromotions,
  notifyContactReclassifications,
} from 'utils/promotionToast';
import { membershipTypeOptionsFor } from 'components/admin/AthleteFormModal/AthleteFormModal';
import { clubForId } from 'utils/clubFeatures';
import { GROUPING_BASIS, groupingBasisOf, YEAR_LEVEL_OPTIONS } from 'utils/groupLabel';

import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomSelect from 'components/common/CustomSelect';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import CustomDatePicker from 'components/common/CustomDatePicker/CustomDatePicker';
import LabelText from 'components/common/LabelText';

// Small dialog used by AthleteProfiles to link an EXISTING platform AthleteProfile
// to the current club. The athlete's identity (name, birth date, gender) is already
// known and shown read-only; the admin only sets the per-club fields.
//
// Membership type defaults from the member's club role: a Guest member's athlete
// defaults to Guest, otherwise Full. Guest is no longer offered (the guests feature
// is hidden for every club, 2026-10-11), so the Membership Type field is shown only
// for a member who is already a Guest, whose athlete may be a Guest or a Full Member.

const defaultMembershipTypeForMemberRole = (memberRole) =>
  memberRole === 'Guest' ? 'Guest' : 'Full';

// What the dialog is given when the page has no team list for it: a club without teams.
const NO_CLUB_TEAMS = { hasTeams: false, teams: [], teamWords: null };

const LinkAthleteToClubDialog = ({
  show,
  athlete,             // { athleteID, firstName, lastName, birthDate, gender, currentClubs }
  memberName,
  memberRole,
  clubId,
  onClose,
  onSuccess,
  clubTeams = NO_CLUB_TEAMS,
}) => {
  // A club that has teams (a school's houses or factions) offers a Team field, named in the club's own word.
  // It is optional: blank is no team. The page loads the club's teams once and passes them in.
  const { hasTeams, teams, teamWords } = clubTeams;
  const teamOptions = teams.map((team) => ({ value: team.teamID, label: team.name }));

  // Full Member only, or Full Member and Guest for a member who is already a Guest.
  const membershipTypeOptions = membershipTypeOptionsFor(
    defaultMembershipTypeForMemberRole(memberRole)
  );

  // A club that groups by year level asks for the student's Year Level. The athlete is new to this club, so
  // there is no stored year to start from, and the server never works one out from the birth date.
  const club = useSelector((state) => clubForId(state.user, clubId));
  const groupsByYearLevel = groupingBasisOf(club) === GROUPING_BASIS.YEAR_LEVEL;

  const [formValues, setFormValues] = useState({
    clubAthleteNumber: '',
    participationStatus: 'YES',
    membershipType: defaultMembershipTypeForMemberRole(memberRole),
    externalClubName: '',
    expiresAt: '',
    clubRFIDNumber: '',
    yearLevel: '',
    teamId: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Hint shown beneath the Club ID Number field when we've suggested a number
  // from the athlete's prior race history in this club, or when the prior number
  // is already in use by someone else.
  const [numberHint, setNumberHint] = useState(null);

  useEffect(() => {
    if (!show) return;
    setFormValues({
      clubAthleteNumber: '',
      participationStatus: 'YES',
      membershipType: defaultMembershipTypeForMemberRole(memberRole),
      externalClubName: '',
      expiresAt: '',
      clubRFIDNumber: '',
      yearLevel: '',
      teamId: '',
    });
    setErrors({});
    setNumberHint(null);

    // Suggest the athlete's previously-used Club ID Number (sourced from their
    // most recent race result in this club) when available. The admin can accept
    // the suggestion or override it.
    if (athlete?.athleteID && clubId) {
      let cancelled = false;
      (async () => {
        try {
          const res = await getSuggestedClubAthleteNumber(clubId, athlete.athleteID);
          if (cancelled) return;
          const { suggestedNumber, reason } = res.data || {};
          if (suggestedNumber) {
            setFormValues((prev) => ({
              ...prev,
              clubAthleteNumber: String(suggestedNumber),
            }));
            setNumberHint({
              kind: 'suggested',
              text: `Pre-filled with their previous number ${suggestedNumber}`,
            });
          } else if (reason === 'previous-number-in-use') {
            setNumberHint({
              kind: 'warning',
              text: 'Their previous number is now used by another athlete — please pick a new one.',
            });
          }
        } catch (err) {
          // Suggestion is best-effort; failures should not block the dialog.
          console.warn('[LinkAthleteToClub] suggestion lookup failed:', err);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
  }, [show, memberRole, athlete?.athleteID, clubId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const next = {};
    if (
      !formValues.clubAthleteNumber ||
      parseInt(formValues.clubAthleteNumber, 10) <= 0
    ) {
      next.clubAthleteNumber =
        'Club ID number is required and must be greater than 0';
    }
    if (!formValues.membershipType) {
      next.membershipType = 'Membership type is required';
    }
    if (!formValues.participationStatus) {
      next.participationStatus = 'Participation status is required';
    }
    if (formValues.membershipType === 'Guest' && !formValues.expiresAt) {
      next.expiresAt = 'Expiration date is required for Guest athletes';
    }
    if (groupsByYearLevel && formValues.yearLevel === '') {
      next.yearLevel = 'Year level is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        membershipType: formValues.membershipType,
        participationStatus: formValues.participationStatus,
        clubAthleteNumber: parseInt(formValues.clubAthleteNumber, 10),
        ...(formValues.clubRFIDNumber
          ? { clubRFIDNumber: formValues.clubRFIDNumber }
          : {}),
        ...(groupsByYearLevel
          ? { yearLevel: parseInt(formValues.yearLevel, 10) }
          : {}),
        // Sent only by a club that has teams; an empty value means no team.
        ...(hasTeams ? { teamID: formValues.teamId || '' } : {}),
        ...(formValues.membershipType === 'Guest'
          ? {
              expiresAt: formValues.expiresAt,
              ...(formValues.externalClubName
                ? { externalClubName: formValues.externalClubName }
                : {}),
            }
          : {}),
      };
      const resp = await linkAthleteToClub(clubId, athlete.athleteID, payload);
      toast.success(
        `${athlete.firstName} ${athlete.lastName} linked to this club`
      );
      notifyContactPromotions(resp);
      notifyContactReclassifications(resp);
      onSuccess?.();
    } catch (error) {
      console.error('Link athlete error:', error);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        error?.response?.data ||
        error?.message ||
        'Failed to link athlete to this club';
      toast.error(typeof msg === 'string' ? msg : 'Failed to link athlete');
    } finally {
      setLoading(false);
    }
  };

  if (!show || !athlete) return null;

  const athleteFullName = `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim();
  const otherClubsLine =
    athlete.currentClubs && athlete.currentClubs.length > 0
      ? `Currently in: ${athlete.currentClubs.join(', ')}`
      : 'Not currently in any club';

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Add Athlete to This Club</h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.memberInfo}>
            <LabelText
              label="Athlete:"
              text={athleteFullName}
              style={{
                marginBottom: '0.5rem',
                padding: '0.75rem',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
              }}
            />
            <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0 0 1rem 0' }}>
              {otherClubsLine}. Linking adds them to <strong>{memberName}</strong>
              &apos;s family in this club. The athlete profile itself (name, birth
              date, gender) is unchanged.
            </p>
          </div>

          <div className={styles.form}>
            <div className="twoColumnRow">
              <div className="flex1">
                <CustomInput
                  label="Club ID Number (Required)"
                  name="clubAthleteNumber"
                  type="number"
                  value={String(formValues.clubAthleteNumber || '')}
                  placeholder="e.g. 751"
                  onChange={handleInputChange}
                  error={errors.clubAthleteNumber}
                />
                {numberHint && (
                  <p
                    style={{
                      /* CustomInput has margin-bottom: 20px on its wrapper,
                         which pushes anything that follows too far down.
                         Pull the hint up snug to the input, then add real
                         space below it before the next row. */
                      margin: '-16px 2px 14px',
                      fontSize: '0.8125rem',
                      color: numberHint.kind === 'warning' ? '#b45309' : '#0f5132',
                    }}
                  >
                    {numberHint.text}
                  </p>
                )}
              </div>
              <div className="flex1">
                <CustomSelect
                  label="Participation Status"
                  value={formValues.participationStatus}
                  onChange={handleInputChange}
                  name="participationStatus"
                  options={participationStatusOptions}
                  error={errors.participationStatus}
                />
              </div>
            </div>

            {(groupsByYearLevel || hasTeams) && (
              <div className="twoColumnRow">
                {groupsByYearLevel && (
                  <div className="flex1">
                    <CustomSelect
                      label="Year Level"
                      value={formValues.yearLevel}
                      onChange={handleInputChange}
                      name="yearLevel"
                      options={YEAR_LEVEL_OPTIONS}
                      error={errors.yearLevel}
                    />
                  </div>
                )}
                {hasTeams && (
                  <div className="flex1">
                    <CustomSelect
                      label={teamWords.team}
                      value={formValues.teamId}
                      onChange={handleInputChange}
                      name="teamId"
                      options={teamOptions}
                    />
                  </div>
                )}
                {!(groupsByYearLevel && hasTeams) && <div className="flex1" />}
              </div>
            )}

            {membershipTypeOptions.length > 1 && (
              <div
                style={
                  formValues.membershipType !== 'Guest'
                    ? { paddingBottom: '6rem' }
                    : {}
                }
              >
                <CustomDropdown
                  label="Membership Type"
                  name="membershipType"
                  value={formValues.membershipType}
                  onChange={handleDropdownChange}
                  options={membershipTypeOptions}
                  error={errors.membershipType}
                />
              </div>
            )}

            {formValues.membershipType === 'Guest' && (
              <div className="twoColumnRow">
                <div className="flex1">
                  <CustomInput
                    label="External Club Name"
                    name="externalClubName"
                    type="text"
                    value={String(formValues.externalClubName || '')}
                    placeholder=""
                    onChange={handleInputChange}
                    error={errors.externalClubName}
                  />
                </div>
                <div className="flex1">
                  <CustomDatePicker
                    label="Membership Expiration Date"
                    name="expiresAt"
                    value={formValues.expiresAt}
                    onChange={handleInputChange}
                    error={errors.expiresAt}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Linking...' : 'Add to Club'}
          </button>
        </div>
      </div>
    </div>
  );
};

LinkAthleteToClubDialog.propTypes = {
  show: PropTypes.bool.isRequired,
  athlete: PropTypes.shape({
    athleteID: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    birthDate: PropTypes.string,
    gender: PropTypes.string,
    currentClubs: PropTypes.arrayOf(PropTypes.string),
  }),
  memberName: PropTypes.string,
  memberRole: PropTypes.string,
  clubId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  // The club's teams, from useClubTeams: { hasTeams, teams, teamWords }. Left out for a club without teams.
  clubTeams: PropTypes.shape({
    hasTeams: PropTypes.bool,
    teams: PropTypes.array,
    teamWords: PropTypes.object,
  }),
};

export default LinkAthleteToClubDialog;
